import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import SubServiceGrid from '../../components/seo/SubServiceGrid';
import { getGallery } from '../../seo/galleryConfig';

const refrigSubServices = [
  { label: 'Commercial Refrigeration', blurb: 'Walk-in cold rooms, display chillers, restaurant fridges, ice machines.' },
  { label: 'Refrigeration Repairs', blurb: 'Compressor faults, gas leaks, temperature drift — diagnosed and fixed.' },
  { label: 'Refrigeration Maintenance', blurb: 'Planned contracts to prevent stock loss for restaurants, shops, gyms.' },
  { label: 'Refrigeration Servicing', blurb: 'Scheduled servicing to keep efficiency high and warranties valid.' },
  { label: 'Emergency Refrigeration Breakdown Response', blurb: '24/7 callout when stock loss or trading downtime is on the line.' },
];

const faqs = [
  {
    q: 'How quickly can you respond to a commercial refrigeration breakdown?',
    a: 'For genuine emergencies — failed display chiller during trading hours, walk-in cold room losing temperature, ice machine down at a busy bar — we target under 2 hours during the day and under 4 hours overnight. Stock loss is real money, and we know it.'
  },
  {
    q: 'Do you cover both commercial and domestic refrigeration?',
    a: 'Yes — most of our work is commercial (restaurants, cafes, shops, gyms, small offices with kitchens) but we also handle larger domestic refrigeration issues such as American-style fridges, wine coolers, and specialist temperature-controlled units.'
  },
  {
    q: 'Are you F-Gas certified?',
    a: 'Yes — all refrigerant work is carried out by F-Gas certified engineers as required by UK law. We hold the equipment, the certifications and the insurance to legally service every system we attend.'
  },
  {
    q: 'How often should commercial refrigeration be serviced?',
    a: 'Twice yearly minimum for heavily-used units. A pre-summer service catches issues before peak demand; a pre-Christmas service prepares for the busiest trading period. Many of our customers run quarterly services on critical systems.'
  },
  {
    q: 'Do you offer service contracts for restaurants and shops?',
    a: 'Yes. Our refrigeration maintenance contracts include scheduled servicing, priority emergency response (with a tighter SLA than walk-in customers), and consolidated monthly billing. We work with a number of independent restaurants and small chains across the South-East.'
  },
];

const relatedServices = [
  { slug: 'air-conditioning', label: 'Air Conditioning', blurb: 'AC installation, servicing and repair.' },
  { slug: 'property-maintenance', label: 'Property Maintenance', blurb: 'Bundle refrigeration servicing into a wider care contract.' },
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: 'Other 24/7 emergency response services.' },
];

const RefrigerationPage = () => (
  <ServicePageLayout
    seoTitle="Refrigeration Services in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Commercial refrigeration installation, servicing, maintenance and emergency breakdown response across Kent, London and Essex. F-Gas certified. Call +44 330 122 1094."
    canonicalPath="/services/refrigeration"
    keywords="refrigeration services, commercial refrigeration, refrigeration repairs, refrigeration servicing, refrigeration maintenance, refrigeration emergency, refrigeration Kent, refrigeration London, refrigeration Essex"
    pageTitle="Refrigeration Services for Kent, London & Essex"
    serviceName="Refrigeration Services"
    serviceSlug="refrigeration"
    heroSubheading="Commercial cold rooms, display chillers, restaurant systems — installed, serviced and repaired."
    intro="When a commercial fridge fails, the cost is measured in lost stock, lost trade, and frustrated customers. H.A.R.P Solutions provides full refrigeration services — installation, planned maintenance, repair, and 24/7 emergency breakdown response — for restaurants, shops, gyms and small commercial premises across Kent, London and Essex. F-Gas certified engineers, on-call availability, transparent pricing."
    bullets={[
      'F-Gas certified engineers',
      'Commercial cold rooms, display chillers, ice machines',
      '24/7 emergency breakdown response',
      'Planned maintenance contracts',
      'Stock-loss prioritised SLAs for contracts',
      'Restaurant, shop, gym and office coverage',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('refrigeration')}
  >
    <h2>Refrigeration That Stays Running</h2>
    <p>
      Refrigeration failure is uniquely expensive: lost stock has to be written off in hours, customers leave when display chillers are
      empty, and the longer a system runs in degraded mode the more damage it does to itself. We work hard to make sure our customers
      never face the worst of it — through proper installation, structured servicing, and rapid emergency response when the unexpected
      happens anyway.
    </p>

    <h2>Our Refrigeration Sub-Services</h2>
    <SubServiceGrid
      title="Choose A Service Below"
      subtitle="Each item is a service we provide. Call us to discuss your specific situation and we'll quote accordingly."
      items={refrigSubServices}
    />

    <h2 className="!mt-12">Who We Work With</h2>
    <ul>
      <li><strong>Independent restaurants and cafés</strong> — cold rooms, prep fridges, display chillers, ice machines.</li>
      <li><strong>Small retail shops</strong> — display fridges, drinks coolers, ice cream cabinets.</li>
      <li><strong>Gyms and leisure venues</strong> — beverage fridges, smoothie-bar units, post-workout drinks displays.</li>
      <li><strong>Offices with kitchens</strong> — staff fridges, water coolers, ice machines.</li>
      <li><strong>Small commercial kitchens</strong> — walk-in cold rooms, blast chillers, freezer cabinets.</li>
      <li><strong>Specialist domestic systems</strong> — American-style fridges, wine coolers, dual-temperature units.</li>
    </ul>

    <h2>Why Planned Maintenance Pays For Itself</h2>
    <p>
      A refrigeration maintenance contract typically pays for itself within the first prevented emergency callout. Beyond cost, planned
      maintenance gives you:
    </p>
    <ul>
      <li>Manufacturer warranty preservation — many warranties require evidence of annual servicing.</li>
      <li>Stable energy usage — properly serviced systems run at correct refrigerant levels and consume markedly less electricity.</li>
      <li>Cleaner air output — coils and filters are kept clean, food and drink storage stays hygienic.</li>
      <li>Priority emergency response — contract customers jump the queue when something fails.</li>
      <li>Predictable budgeting — one monthly figure replaces unpredictable callout invoices.</li>
    </ul>

    <h2>Emergency Breakdown Response</h2>
    <p>
      Restaurants and shops cannot wait until the next working day to get a chiller working. We run a 24/7 line specifically for
      refrigeration emergencies — call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> and we will dispatch the nearest
      engineer with the most common gas, control boards and seal kits on board.
    </p>
    <p>
      Where the unit is unrepairable on-site, we can usually arrange temporary cold storage (loan freezers, cool boxes, dry ice) within a
      few hours so trading can continue.
    </p>

    <h2>Book A Quote</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for a free site survey and refrigeration quote, or use the
      form below. For wider commercial property care see our <a href="/services/property-maintenance">property maintenance service</a>.
    </p>
  </ServicePageLayout>
);

export default RefrigerationPage;
